/**
 * Module-scope constants, static panel catalogue, and storage-key
 * definitions consumed by `LobbyClient.tsx`.
 *
 * Extracted verbatim from `LobbyClient.tsx` to reduce the render
 * module's footprint without changing runtime behaviour. Every value
 * here is either:
 *   - a pure constant (storage keys, thresholds, timings),
 *   - a frozen catalogue (`ALL_PANELS`, `MOBILE_PANEL_TITLES`),
 *   - a deterministic helper with zero React state
 *     (`getDefaultVisiblePanels`).
 *
 * No hooks, no side effects, no cross-cutting imports from the lobby
 * render surface — keep it that way so this file stays a leaf.
 *
 * See inline JSDoc on each export for the full rationale originally
 * threaded through `LobbyClient.tsx`; the prose was kept verbatim so
 * `git blame` on the lobby still surfaces the same history when the
 * constants are re-read.
 */

import { type ReactNode } from 'react';
import {
  Activity,
  LayoutGrid,
  Map,
  MessageSquare,
  Mountain,
  Sparkles,
  TrendingUp,
  Volume2,
} from 'lucide-react';
import { type MobilePanelId } from './panels/MobilePanelChipRail';

export const FLOATING_PANEL_FADE = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.28, ease: [0.2, 0.8, 0.2, 1] as const },
};

export type PanelId =
  | 'rewards'
  | 'mytiles'
  | 'holders'
  | 'yield'
  | 'chat'
  | 'states'
  | 'sounds'
  | 'skybox';

// Activity is no longer a draggable panel — it's the persistent ticker
// strip mounted directly under LobbyNav (`<GlobalActivityTicker variant="lobby" />`).
// Hotkeys 1–5 stay attached to user-facing panels; 6–8 are dev panels.
//
// Chat lives at hotkey 5 because it's the only HUD pill that can drive a
// visible *sidebar* (xl+) instead of a draggable panel — toggling it lets
// users reclaim that vertical strip for the canvas.
export const ALL_PANELS: {
  id: PanelId;
  label: string;
  icon: ReactNode;
  hotkey: string;
  dev?: boolean;
}[] = [
  { id: 'mytiles',  label: 'My Tiles', icon: <LayoutGrid className="h-3 w-3" />,  hotkey: '1' },
  { id: 'rewards',  label: 'Activity', icon: <Activity className="h-3 w-3" />,     hotkey: '2' },
  { id: 'holders',  label: 'Holders',  icon: <Map className="h-3 w-3" />,         hotkey: '3' },
  { id: 'yield',    label: 'Yield',    icon: <TrendingUp className="h-3 w-3" />,  hotkey: '4' },
  { id: 'chat',     label: 'Chat',     icon: <MessageSquare className="h-3 w-3" />, hotkey: '5' },
  { id: 'states',   label: 'States',   icon: <Sparkles className="h-3 w-3" />,    hotkey: '6', dev: true },
  { id: 'sounds',   label: 'Sounds',   icon: <Volume2 className="h-3 w-3" />,     hotkey: '7', dev: true },
  { id: 'skybox',   label: 'Skybox',   icon: <Mountain className="h-3 w-3" />,    hotkey: '8', dev: true },
];

// Every panel is hidden by default for a brand-new user (no
// `VISIBLE_PANELS_STORAGE_KEY` entry yet) so the board paints clean and
// unobstructed on first visit — the same starting point in both the 2D
// and 3D views. Once the user opens a panel from the bottom-right HUD
// pill rail (or its hotkey), the persistence effect below writes their
// explicit set to localStorage and that becomes the source of truth on
// subsequent loads — so this default only affects first-touch sessions.
// Users always keep the metadata panel (`PinnedTitlePanel`, mounted
// unconditionally outside the `visiblePanels` system) for orientation.
export const DEFAULT_VISIBLE_PANELS: PanelId[] = [];

/**
 * Resolve the first-time default panel set for a fresh session.
 *
 * The result is only consulted when there is no saved entry in
 * `VISIBLE_PANELS_STORAGE_KEY` yet — once the user toggles anything,
 * the persistence effect writes their explicit set to localStorage
 * and that becomes the source of truth.
 *
 * Tutorial mode returns the frozen tutorial set: the choreographed
 * script (see `tutorial-script.ts`) depends on specific panels being
 * open at specific steps, so it can't start from the empty default.
 * Every other session starts with no panels open
 * (`DEFAULT_VISIBLE_PANELS`) regardless of viewport, so the canvas is
 * unobstructed on first paint in both the 2D and 3D views.
 */
export function getDefaultVisiblePanels(forTutorial: boolean): PanelId[] {
  return forTutorial ? TUTORIAL_DEFAULT_VISIBLE_PANELS : DEFAULT_VISIBLE_PANELS;
}

// Tutorial mode runs against a tightly choreographed script (see
// `tutorial-script.ts`) that calls out specific panels in a specific
// order: My Tiles, Activity, Holders, Yield. Adding Chat (or anything
// the lobby's "everything open" default introduces in the future)
// would put a panel on screen the script never references and breaks
// the carefully tuned overlay positioning. Keep the tutorial's
// initial visibility frozen here so changes to the regular-user
// default never silently contaminate onboarding — the storage
// read/write is also gated on `!tutorial` so this set is the sole
// source of truth for tutorial sessions.
export const TUTORIAL_DEFAULT_VISIBLE_PANELS: PanelId[] = ['mytiles', 'rewards', 'holders', 'yield'];

export const DEV_PANEL_IDS = new Set<PanelId>(['states', 'sounds', 'skybox']);
export const DEV_MODE_STORAGE_KEY = 'takeover-lobby-dev-mode';

/**
 * Build-time toggle for the lobby's dev-only HUD layer (the `~` keypress
 * that exposes the states/sounds/skybox panels, the DEV badge, and the
 * Shift+letter skybox preset shortcuts).
 *
 * Resolved against `NEXT_PUBLIC_APP_MODE` (not `NODE_ENV`) because the
 * lobby is built with `NODE_ENV=production` for both staging and
 * production — but staging is where QA exercises the dev panels, so we
 * key off the explicit app-mode flag instead.
 *
 * In production, even users who somehow flipped `localStorage` to
 * `'1'` on a staging build don't get the panels: the hydration pass
 * below ignores the stored value, the `setDevMode(true)` keypath is
 * unreachable, and `devMode` therefore stays at its `false` default
 * forever. The dev panels and DEV badge gate on `devMode` so they
 * never render either.
 */
export const DEV_MODE_AVAILABLE = process.env.NEXT_PUBLIC_APP_MODE !== 'production';

export const MOBILE_PANEL_TITLES: Record<MobilePanelId, string> = {
  // `board` never surfaces as an overlay title — it's the canvas-home
  // chip — but we keep it in the record so the union type stays
  // exhaustive and a future chip-specific heading (e.g. for the panel
  // picker) can consume the same copy source. Similarly `chat` is a
  // navigation chip (routes to `/chat`) rather than an overlay, but the
  // union demands an entry so we keep a sensible fallback title in case
  // a future surface needs to render it.
  board: 'Board',
  chat: 'Chat',
  info: 'Board Info',
  rewards: 'Activity',
  holders: 'Top Holders',
  yield: 'Yield',
};

/** Search-param sentinel that flips the central viewport into the
 *  full-bleed Lobby directory. Used by the mobile Lobby tab — present
 *  means "show the board directory"; absent means "show the board
 *  canvas". Kept as a query rather than a route so navigating to a
 *  specific board cleanly clears it. */
export const LOBBY_MODE_PARAM = 'mode';
export const LOBBY_MODE_VALUE = 'lobby';

/** Explicit "show the canvas" override that beats the mobile app-root's
 *  lobby default (`isMobile && pathname === '/lobby'` defaults to lobby;
 *  `/` is matched too for legacy links). Without it, tapping the
 *  Boardroom card from the mobile lobby would loop straight back into
 *  the directory because both share the `/lobby` URL. Desktop ignores
 *  this value — it has no implicit lobby. */
export const BOARD_MODE_VALUE = 'board';

/** How long a "first tap" stays primed before we treat a second tap as a
 *  fresh first tap again. Long enough that users can read the tooltip;
 *  short enough that stale pending taps don't open the drawer
 *  unexpectedly. */
export const TILE_TAP_WINDOW_MS = 3000;

export const BOARD_SORT_STORAGE_KEY = 'takeover-lobby-board-sort';

/**
 * Persists the set of HUD panels the user currently has open (e.g. yield,
 * mytiles, holders, chat) so their last-chosen layout survives:
 *   - hard refreshes,
 *   - leaving the `(lobby)` route group (e.g. visiting `/chat` or `/tutorial`)
 *     and coming back, which unmounts `LobbyClient` and would otherwise
 *     drop the in-memory `visiblePanels` set,
 *   - other-tab/return-trip sessions.
 *
 * Stored as a JSON-encoded array of `PanelId` strings. We validate every
 * entry against `ALL_PANELS` on read, so adding/removing a panel id from
 * the union doesn't poison hydration on existing users — unknown ids are
 * silently dropped. An empty array is a valid, intentional state ("user
 * hid everything") and is restored verbatim — that's the point of the
 * "if I hide everything, the same should apply" affordance.
 *
 * Tutorial mode (`useTutorialOptional`) deliberately skips read+write so
 * the scripted onboarding stays deterministic; it doesn't pollute the
 * user's real lobby preference and isn't seeded from it either.
 *
 * The suffix is bumped whenever the first-touch default changes, because
 * a saved layout always wins over the default on subsequent loads — so
 * without a bump, existing users who never explicitly closed a panel
 * would stay locked to the *old* default forever (the first-touch seed
 * was written to localStorage on their very first visit). Bumping forces
 * a one-time re-seed from the current default; a user's ongoing
 * preferences from then on persist under the new key as expected.
 *
 * History:
 *   - (unsuffixed) — legacy "everything open" on every viewport.
 *   - `:v2` — "everything open" on wide desktop, empty on narrow.
 *   - `:v3` — empty everywhere (all panels hidden by default, both 2D
 *     and 3D). This retires the `:v2` "everything open" seeds so the
 *     clean-board default actually reaches returning desktop users.
 */
export const VISIBLE_PANELS_STORAGE_KEY = 'takeover-lobby-visible-panels:v3';
